/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { environmentsGetEnvironment } from "../funcs/environmentsGetEnvironment.js";
import { environmentsListEnvironments } from "../funcs/environmentsListEnvironments.js";
import { environmentsListEnvironmentVariables } from "../funcs/environmentsListEnvironmentVariables.js";
import { environmentsRemoveEnvironmentVariables } from "../funcs/environmentsRemoveEnvironmentVariables.js";
import { environmentsSetEnvironmentVariables } from "../funcs/environmentsSetEnvironmentVariables.js";
import { environmentsUpdateSettings } from "../funcs/environmentsUpdateSettings.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import * as components from "../models/components/index.js";
import { unwrapAsync } from "../types/fp.js";

export class Environments extends ClientSDK {
  /**
   * Get environment
   *
   * @remarks
   * Retrieve a single environment by its id.
   *
   * Use this to fetch environment details after creation or to verify an environment exists before performing operations.
   *
   * **Required Permissions**
   *
   * Your root key must have one of the following permissions:
   * - `environment.*.read_environment` (to read any environment)
   * - `environment.<environment_id>.read_environment` (to read a specific environment)
   */
  async getEnvironment(
    request: components.V1EnvironmentsGetEnvironmentRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1EnvironmentsGetEnvironmentResponseBody> {
    return unwrapAsync(environmentsGetEnvironment(
      this,
      request,
      options,
    ));
  }

  /**
   * List environment variables
   *
   * @remarks
   * Retrieve the environment variables for an environment.
   *
   * Identify the environment by its project, app, and environment identifiers.
   * Results are ordered by variable id and paginated; when `hasMore` is true,
   * pass the returned `cursor` to fetch the next page.
   *
   * `recoverable` variables are returned with their decrypted plaintext value.
   * `writeonly` variables never expose a value: only the key, kind, and
   * description are returned.
   *
   * **Required Permissions**
   *
   * Your root key must have one of the following permissions:
   * - `environment.*.read_environment_variables` (for any environment)
   * - `environment.<environment_id>.read_environment_variables` (for a specific environment)
   */
  async listEnvironmentVariables(
    request: components.V1EnvironmentsListEnvironmentVariablesRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1EnvironmentsListEnvironmentVariablesResponseBody> {
    return unwrapAsync(environmentsListEnvironmentVariables(
      this,
      request,
      options,
    ));
  }

  /**
   * List environments
   *
   * @remarks
   * Retrieve the environments within an app.
   *
   * Use this to enumerate every environment in an app. Identify the app by its project slug and app slug. Results are ordered by environment id. An app has only a handful of environments, so all of them are returned in a single response.
   *
   * **Required Permissions**
   *
   * Your root key must have the following permission:
   * - `environment.*.read_environment` (to read environments in any app)
   */
  async listEnvironments(
    request: components.V1EnvironmentsListEnvironmentsRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1EnvironmentsListEnvironmentsResponseBody> {
    return unwrapAsync(environmentsListEnvironments(
      this,
      request,
      options,
    ));
  }

  /**
   * Remove environment variables
   *
   * @remarks
   * Remove environment variables from an environment in a single atomic request.
   *
   * This operation only deletes keys by name. Keys in the payload that exist are
   * removed; keys that are not present are ignored, since their absence already
   * matches the requested state. To replace or update values, use
   * `setEnvironmentVariables` instead. The whole operation is atomic: if any part
   * fails the environment is left unchanged.
   *
   * Duplicate keys in the payload collapse to a single removal. Values are never
   * read or returned.
   *
   * **Required Permissions**
   *
   * Your root key must have one of the following permissions:
   * - `environment.*.remove_environment_variables` (for any environment)
   * - `environment.<environment_id>.remove_environment_variables` (for a specific environment)
   */
  async removeEnvironmentVariables(
    request: components.V1EnvironmentsRemoveEnvironmentVariablesRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1EnvironmentsRemoveEnvironmentVariablesResponseBody> {
    return unwrapAsync(environmentsRemoveEnvironmentVariables(
      this,
      request,
      options,
    ));
  }

  /**
   * Set environment variables
   *
   * @remarks
   * Create or update environment variables for an environment in a single atomic
   * request.
   *
   * By default this is an upsert: each variable in the payload is created if new
   * or fully overwritten if the key already exists, and any variable not in the
   * payload is left untouched. This lets you change one variable without
   * re-sending the others, which matters for write-only secrets you can no longer
   * read back.
   *
   * Set `prune: true` to make it a full replace instead: after upserting, every
   * variable not in the payload is deleted. Sending `prune: true` with an empty
   * `variables` list resets the environment by deleting every variable.
   *
   * Each variable is written exactly as sent, never merged, so omitted optional
   * fields fall back to their defaults rather than the previous value. Values are
   * always encrypted at rest. Set `kind: recoverable` to allow a value to be read
   * back; it defaults to `writeonly`, which can never be read back through the API.
   *
   * **Required Permissions**
   *
   * Your api key must have one of the following permissions:
   * - `environment.*.set_environment_variables` (for any environment)
   * - `environment.<environment_id>.set_environment_variables` (for a specific environment)
   */
  async setEnvironmentVariables(
    request: components.V1EnvironmentsSetEnvironmentVariablesRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1EnvironmentsSetEnvironmentVariablesResponseBody> {
    return unwrapAsync(environmentsSetEnvironmentVariables(
      this,
      request,
      options,
    ));
  }

  /**
   * Update environment settings
   *
   * @remarks
   * Update the build, runtime, and regional settings for an environment.
   *
   * All settings fields are optional. Omit a field to leave it unchanged. For
   * nullable fields (`dockerfile`, `healthcheck`, `openapiSpecPath`), send null
   * to clear the value. When `regions` is present it replaces the full set of
   * regions for the environment.
   *
   * **Required Permissions**
   *
   * Your root key must have one of the following permissions:
   * - `environment.*.update_environment` (to update any environment)
   * - `environment.<environment_id>.update_environment` (to update a specific environment)
   */
  async updateSettings(
    request: components.V1EnvironmentsUpdateSettingsRequestBody,
    options?: RequestOptions,
  ): Promise<components.V1EnvironmentsUpdateSettingsResponseBody> {
    return unwrapAsync(environmentsUpdateSettings(
      this,
      request,
      options,
    ));
  }
}
